'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocale } from 'next-intl'
import { v4 as uuid } from 'uuid'
import Uppy, { type UppyFile, Uppy as UppyType } from '@uppy/core'
import { Dashboard } from '@uppy/react'
import French from '@uppy/locales/lib/fr_FR'
import English from '@uppy/locales/lib/en_US'
import Dutch from '@uppy/locales/lib/nl_NL'
import Transloadit, {
  COMPANION_ALLOWED_HOSTS,
  COMPANION_URL,
} from '@uppy/transloadit'
import Url from '@uppy/url'
import GoogleDrive from '@uppy/google-drive'

import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'
import '@uppy/url/dist/style.min.css'

const createUppy = (maxNumberOfFiles: number): UppyType => {
  const uppy = new Uppy({
    locale: French,
    restrictions: {
      maxFileSize: 10000000,
      maxNumberOfFiles,
      allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    },
    allowMultipleUploadBatches: true,
  })

  uppy
    .use(Transloadit, {
      async assemblyOptions() {
        const response = await fetch('/api/transloadit', {
          method: 'POST',
        })
        if (!response.ok) {
          throw new Error('Unable to create upload signature')
        }
        return response.json()
      },
    })
    .use(Url, {
      companionUrl: COMPANION_URL,
      companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      companionKeysParams: {
        key: process.env.NEXT_PUBLIC_TRANSLOADIT_API_KEY ?? '',
        credentialsName:
          process.env.NEXT_PUBLIC_TRANSLOADIT_TEMPLATE_NAME ?? '',
      },
    })
    .use(GoogleDrive, {
      companionUrl: COMPANION_URL,
      companionAllowedHosts: COMPANION_ALLOWED_HOSTS,
      companionKeysParams: {
        key: process.env.NEXT_PUBLIC_TRANSLOADIT_API_KEY ?? '',
        credentialsName:
          process.env.NEXT_PUBLIC_TRANSLOADIT_GOOGLE_DRIVE_CREDENTIALS_NAME ??
          '',
      },
    })

  uppy.on('file-added', (file) => uppy.setFileMeta(file.id, { id: uuid() }))

  return uppy
}

type UppyDashboardProps = {
  pictures: string[]
  maxNumberOfFiles?: number
  onUploadSuccess?: (uploadedPictures: string[]) => Promise<void> | void
}

export const UppyDashboard = ({
  pictures,
  maxNumberOfFiles = 5,
  onUploadSuccess,
}: UppyDashboardProps) => {
  const locale = useLocale()

  // Refs to track uploaded pictures between Uppy events.
  const uploadedPicturesRef = useRef<string[]>([])

  const onSuccess = useCallback((file: UppyDashboardFile | undefined) => {
    const id = typeof file?.meta?.id === 'string' ? file.meta.id : null
    if (!id) return

    const extension = (file?.extension ?? 'jpg').toLowerCase()
    const picture = `${id}.${extension}`

    // Add to the batch of uploaded pictures
    uploadedPicturesRef.current.push(picture)
  }, [])

  const onComplete = useCallback(
    (uploadedPictures: string[]) => {
      if (onUploadSuccess && uploadedPictures.length > 0) {
        onUploadSuccess(uploadedPictures)
      }
      uploadedPicturesRef.current = []
    },
    [onUploadSuccess],
  )

  // IMPORTANT: passing an initializer function to prevent Uppy from being reinstantiated on every render.
  const [uppy] = useState(() => createUppy(maxNumberOfFiles))

  useEffect(() => {
    const handleUploadSuccess = (file: UppyDashboardFile | undefined) => {
      onSuccess(file)
    }
    const handleComplete = () => {
      onComplete([...uploadedPicturesRef.current])
    }

    uppy.on('upload-success', handleUploadSuccess)
    uppy.on('complete', handleComplete)

    return () => {
      uppy.off('upload-success', handleUploadSuccess)
      uppy.off('complete', handleComplete)
    }
  }, [onComplete, onSuccess, uppy])

  // Pictures limit
  useEffect(() => {
    uppy.setOptions({
      restrictions: {
        maxNumberOfFiles: Math.max(
          0,
          maxNumberOfFiles - (pictures?.length || 0),
        ),
      },
    })
  }, [maxNumberOfFiles, pictures, uppy])

  // Set the locale
  useEffect(() => {
    const getLocale = () => {
      switch (locale) {
        case 'en':
          return English
        case 'nl':
          return Dutch
        default:
          return French
      }
    }
    uppy.setOptions({
      locale: getLocale(),
    })
  }, [locale, uppy])

  return <Dashboard uppy={uppy} proudlyDisplayPoweredByUppy={false} />
}
type UppyDashboardFile = UppyFile<
  Record<string, unknown>,
  Record<string, never>
>
